import { Component, Input, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DistrictDto, ProvinceDto } from '../../../../generated-model/models';
import { formatRecordedAt, resolveRecordedBy } from '../../../shared/commons/audit-display.util';
import { ProvinceAutocompleteComponent } from '../../../shared/commons/province-autocomplete/province-autocomplete';

@Component({
  selector: 'app-district-modal',
  standalone: true,
  imports: [ReactiveFormsModule, ProvinceAutocompleteComponent],
  templateUrl: './district-modal.html'
})
export class DistrictModal implements OnInit {
  @Input() district: DistrictDto | undefined;
  @Input() viewOnly = false;
  @Input() onSave?: (payload: any, isEditMode: boolean) => Promise<boolean>;

  form: FormGroup;
  isEditMode = false;
  submitted = false;
  isSubmitting = false;
  recordedByDisplay = '-';
  recordedAtDisplay = '-';

  constructor(public activeModal: NgbActiveModal, private fb: FormBuilder) {
    this.form = this.fb.group({
      id: [null],
      code: ['', [Validators.required, Validators.maxLength(4)]],
      title: ['', [Validators.required, Validators.maxLength(255)]],
      provinceId: [null, [Validators.required]],
      ordering: [0, [Validators.max(32767)]],
      state: [1]
    });
  }

  ngOnInit(): void {
    if (this.district) {
      this.isEditMode = true;
      this.form.patchValue({
        ...this.district,
        provinceId: this.district.province?.id ?? null,
      });
      this.recordedByDisplay = resolveRecordedBy(this.district);
      this.recordedAtDisplay = formatRecordedAt(this.district);
    } else {
      this.form.reset({ ordering: 0, state: 1 });
      this.recordedByDisplay = '-';
      this.recordedAtDisplay = '-';
    }

    if (this.viewOnly) {
      this.form.disable();
    }
  }

  save(): void {
    if (this.viewOnly) {
      return;
    }
    this.submitted = true;
    if (this.form.invalid) return;
    const formValue = this.form.getRawValue();
    const payload: DistrictDto = {
      ...(this.district ?? {} as DistrictDto),
      ...formValue,
      province: {
        ...(this.district?.province ?? {} as ProvinceDto),
        id: formValue.provinceId,
      } as ProvinceDto,
    };
    delete (payload as any).provinceId;
    void this.submitAndClose(payload);
  }

  private async submitAndClose(payload: any): Promise<void> {
    if (!this.onSave) {
      this.activeModal.close(payload);
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const isSuccess = await this.onSave(payload, this.isEditMode);
      if (isSuccess) {
        this.activeModal.close(payload);
      }
    } finally {
      this.isSubmitting = false;
    }
  }
}
